import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import {
  groupDoctorsBySpecialization,
  filterDoctorsBySpec,
  countDoctorsBySpec,
} from "../../../utils/doctorCatalog";
import { DOCTORS_BASE, doctorPhotoUrl, filterAssignedBranches } from "./doctorConstants";
import s from "../clinic.module.css";
import u from "../../ui/ui.module.css";
import {
  Button,
  Card,
  Input,
  Select,
  Avatar,
  EmptyState,
  useToast,
  IconPlus,
  IconSearch,
  IconStethoscope,
} from "../../ui";

function DoctorRegistryRow({ d, branchLabel, onClick }) {
  return (
    <button
      type="button"
      className={`${s.registryRow} ${!d.is_active ? s.registryRowBlocked : ""}`}
      onClick={onClick}
    >
      <div className={s.registryPatient}>
        <Avatar
          name={d.full_name}
          src={doctorPhotoUrl(d.photo_url)}
          size="sm"
        />
        <span className={s.registryName}>
          {d.full_name}
          {d.rating_avg != null && (
            <span className={s.registryRating}>
              {" "}
              ★ {Number(d.rating_avg).toFixed(1)}
            </span>
          )}
        </span>
      </div>
      <span className={s.registryMeta}>{d.specialization || "—"}</span>
      <span className={s.registryMeta}>{branchLabel(d)}</span>
      <span className={s.registryMeta}>
        {d.summary?.today_count ?? 0} записей
      </span>
      <span>
        {d.is_active ? (
          <span className={`${u.badge} ${u.badgeGreen}`}>Активен</span>
        ) : (
          <span className={`${u.badge} ${u.badgeRed}`}>Неактивен</span>
        )}
      </span>
    </button>
  );
}

export default function AdminDoctorsList() {
  const navigate = useNavigate();
  const toast = useToast();
  const [q, setQ] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [specFilter, setSpecFilter] = useState("");
  const [specializations, setSpecializations] = useState([]);
  const [doctors, setDoctors] = useState([]);

  useEffect(() => {
    clinicAPI
      .getSpecializations()
      .then((r) => setSpecializations(r.specializations || []))
      .catch(() => setSpecializations([]));
  }, []);

  const loadList = useCallback(() => {
    const p = { include_summary: "true" };
    if (q.trim()) p.q = q.trim();
    if (activeFilter === "true") p.active = "true";
    if (activeFilter === "false") p.active = "false";
    clinicAPI
      .getDoctors(p)
      .then((r) => setDoctors(r.doctors || []))
      .catch((e) =>
        toast.error(e.response?.data?.error || "Ошибка загрузки"),
      );
  }, [q, activeFilter, toast]);

  useEffect(() => {
    loadList();
  }, [activeFilter, loadList]);

  const activeSpecs = useMemo(
    () => specializations.filter((sp) => sp.is_active !== false),
    [specializations],
  );

  const specCounts = useMemo(
    () => countDoctorsBySpec(doctors, specializations),
    [doctors, specializations],
  );

  const filteredDoctors = useMemo(
    () => filterDoctorsBySpec(doctors, specFilter || "all"),
    [doctors, specFilter],
  );

  const doctorGroups = useMemo(() => {
    if (specFilter) {
      const title =
        specFilter === "none"
          ? "Без специализации"
          : activeSpecs.find((x) => x.id === specFilter)?.name || "";
      return [{ key: specFilter, title, items: filteredDoctors }];
    }
    return groupDoctorsBySpecialization(filteredDoctors, specializations);
  }, [filteredDoctors, specializations, specFilter, activeSpecs]);

  const branchLabel = (d) => {
    const names = filterAssignedBranches(d.branches || [])
      .map((b) => b.name)
      .filter(Boolean);
    return names.length ? names.join(", ") : "—";
  };

  return (
    <Card
      title="Тренеры"
      subtitle={`${filteredDoctors.length} из ${doctors.length}`}
      actions={
        <Button size="sm" onClick={() => navigate("/manage/app/team/invite")}>
          <IconPlus width={16} height={16} />
          Пригласить
        </Button>
      }
    >
      <div className={u.toolbar}>
        <div className={u.searchWrap}>
          <IconSearch className={u.searchIcon} width={18} height={18} />
          <Input
            className={u.searchInput}
            placeholder="ФИО, специализация…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadList()}
          />
        </div>
        <Select
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value)}
          style={{ width: 140 }}
        >
          <option value="">Все статусы</option>
          <option value="true">Активные</option>
          <option value="false">Неактивные</option>
        </Select>
        <Button variant="secondary" size="sm" onClick={loadList}>
          Найти
        </Button>
      </div>

      {doctors.length > 0 && (
        <div className={s.doctorSpecFilters}>
          <button
            type="button"
            className={`${s.catalogChip} ${!specFilter ? s.catalogChipActive : ""}`}
            onClick={() => setSpecFilter("")}
          >
            Все
            <span className={s.catalogChipCount}>{specCounts.all}</span>
          </button>
          {activeSpecs.map((sp) => (
            <button
              key={sp.id}
              type="button"
              className={`${s.catalogChip} ${specFilter === sp.id ? s.catalogChipActive : ""}`}
              onClick={() => setSpecFilter(sp.id)}
            >
              {sp.name}
              <span className={s.catalogChipCount}>
                {specCounts[sp.id] ?? 0}
              </span>
            </button>
          ))}
          <button
            type="button"
            className={`${s.catalogChip} ${specFilter === "none" ? s.catalogChipActive : ""}`}
            onClick={() => setSpecFilter("none")}
          >
            Без специализации
            <span className={s.catalogChipCount}>{specCounts.other ?? 0}</span>
          </button>
        </div>
      )}

      {filteredDoctors.length === 0 ? (
        <EmptyState
          icon={IconStethoscope}
          title={doctors.length ? "Никого не нашли" : "Нет тренеров"}
          description={
            doctors.length
              ? "Смените фильтр или параметры поиска"
              : "Пригласите первого специалиста в клуб"
          }
          actionLabel={doctors.length ? undefined : "Пригласить тренера"}
          onAction={
            doctors.length ? undefined : () => navigate("/manage/app/team/invite")
          }
        />
      ) : (
        <div
          className={`${s.registryTable} ${s.registryTableDoctors} ${s.adminDoctorRegistry}`}
        >
          <div className={s.registryHead}>
            <span>Тренер</span>
            <span>Специализация</span>
            <span>Залы</span>
            <span>Сегодня</span>
            <span>Статус</span>
          </div>
          {doctorGroups.map((group, groupIndex) => (
            <section
              key={group.key}
              className={s.adminDoctorGroupSection}
              aria-labelledby={
                !specFilter && group.title
                  ? `doctor-group-${group.key}`
                  : undefined
              }
            >
              {!specFilter && group.title ? (
                <div
                  className={`${s.adminDoctorGroupDivider} ${groupIndex === 0 ? s.adminDoctorGroupDividerFirst : ""}`}
                >
                  <h3
                    id={`doctor-group-${group.key}`}
                    className={s.adminDoctorGroupHeading}
                  >
                    <span className={s.adminDoctorGroupAccent} aria-hidden />
                    <span className={s.adminDoctorGroupLabel}>{group.title}</span>
                    <span className={s.adminDoctorGroupCount}>
                      {group.items.length}
                    </span>
                  </h3>
                </div>
              ) : null}
              <div className={s.adminDoctorGroupRows}>
                {group.items.map((d) => (
                  <DoctorRegistryRow
                    key={d.id}
                    d={d}
                    branchLabel={branchLabel}
                    onClick={() => navigate(`${DOCTORS_BASE}/${d.id}`)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </Card>
  );
}
